import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  academicsService, staffService, studentsService,
  type LessonPlan, type LogbookEntry, type NotebookCheck,
} from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { BookOpen, ClipboardCheck, Download, FileText, Loader2, Plus, Trash2, Upload, PlayCircle, Youtube } from "lucide-react";
import { toast } from "sonner";


// Convert any YouTube URL (watch, youtu.be, shorts, embed) → privacy-enhanced embed URL.
export function youtubeEmbedUrl(url?: string | null): string | null {
  if (!url) return null;
  const u = url.trim();
  let id = "";
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/shorts\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
    /(?:youtube\.com\/live\/)([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = u.match(re);
    if (m) { id = m[1]; break; }
  }
  if (!id && /^[\w-]{11}$/.test(u)) id = u;
  if (!id) return null;
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`;
}

function YouTubePlayer({ url, testId }: { url: string; testId?: string }) {
  const embed = youtubeEmbedUrl(url);
  if (!embed) {
    return <div className="rounded-md border border-yellow-500/40 bg-yellow-50 p-2 text-xs text-yellow-800">Invalid YouTube link.</div>;
  }
  return (
    <div className="relative w-full overflow-hidden rounded-md bg-black" style={{ paddingTop: "56.25%" }} data-testid={testId}>
      <iframe
        className="absolute inset-0 h-full w-full"
        src={embed}
        title="Lesson video"
        frameBorder={0}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
}

// Extract the 11-char YouTube video id from any URL form.
export function youtubeVideoId(url?: string | null): string | null {
  const embed = youtubeEmbedUrl(url);
  if (!embed) return null;
  const m = embed.match(/\/embed\/([\w-]{11})/);
  return m ? m[1] : null;
}

// Thumbnail with a play button; loads the (autoplay) iframe only on click.
function LazyYouTube({ url, testId }: { url: string; testId?: string }) {
  const [playing, setPlaying] = useState(false);
  const id = youtubeVideoId(url);
  if (!id) {
    return <div className="rounded-md border border-yellow-500/40 bg-yellow-50 p-2 text-xs text-yellow-800">Invalid YouTube link.</div>;
  }
  if (playing) {
    return (
      <div className="relative w-full overflow-hidden rounded-md bg-black" style={{ paddingTop: "56.25%" }} data-testid={testId}>
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&autoplay=1`}
          title="Lesson video" frameBorder={0}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative block w-full overflow-hidden rounded-md bg-black"
      style={{ paddingTop: "56.25%" }}
      data-testid={testId}
      aria-label="Play video"
    >
      <img
        src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`}
        alt="Video thumbnail"
        className="absolute inset-0 h-full w-full object-cover opacity-90 transition group-hover:opacity-100"
        loading="lazy"
      />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600/90 shadow-lg transition group-hover:scale-110">
          <PlayCircle className="h-8 w-8 text-white" />
        </span>
      </span>
    </button>
  );
}

function VideoDialog({ title, url, onClose }: { title: string; url: string; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="video-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Youtube className="h-5 w-5 text-red-600" /> {title}</DialogTitle>
          <DialogDescription>Playing inside the portal — no redirect to YouTube.</DialogDescription>
        </DialogHeader>
        <YouTubePlayer url={url} testId="video-dialog-player" />
      </DialogContent>
    </Dialog>
  );
}


type Tab = "plans" | "logbook" | "notebook";

export function AcademicsPage() {
  const { user } = useAuth();
  const isStudentOrParent = user?.role === "student" || user?.role === "parent";
  const [tab, setTab] = useState<Tab>("plans");

  const TABS: { key: Tab; label: string; icon: any }[] = isStudentOrParent
    ? [{ key: "plans", label: "Study Materials", icon: FileText }]
    : [
        { key: "plans", label: "Lesson Plans", icon: FileText },
        { key: "logbook", label: "Daily Logbook", icon: BookOpen },
        { key: "notebook", label: "Notebook Checks", icon: ClipboardCheck },
      ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Academics</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isStudentOrParent
            ? "Study materials & lesson videos shared by your school"
            : "Lesson plans, daily logbook & notebook-checking records"}
        </p>
      </div>

      <div className="flex gap-2 border-b">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                      active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "plans" && <LessonPlansTab user={user} />}
      {tab === "logbook" && !isStudentOrParent && <LogbookTab user={user} />}
      {tab === "notebook" && !isStudentOrParent && <NotebookTab user={user} />}
    </div>
  );
}

// ═══════════════════════ LESSON PLANS ═══════════════════════
function LessonPlansTab({ user }: { user: any }) {
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const isStudentOrParent = user?.role === "student" || user?.role === "parent";
  const [uploading, setUploading] = useState(false);
  const [video, setVideo] = useState<LessonPlan | null>(null);

  const { data: plans = [], isPending } = useQuery({
    queryKey: ["academics", "lesson-plans"],
    queryFn: () => academicsService.listPlans(),
  });

  const del = useMutation({
    mutationFn: (id: string) => academicsService.removePlan(id),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["academics", "lesson-plans"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const handleDownload = async (p: LessonPlan) => {
    try {
      const blob = await academicsService.fetchPlanFile(p.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = p.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e: any) { toast.error(e.message); }
  };

  // Student / parent: read-only "Study Materials" grid with embedded players.
  if (isStudentOrParent) {
    return (
      <div className="space-y-4" data-testid="study-materials">
        {isPending ? <Loader2 className="m-8 h-6 w-6 animate-spin text-primary" /> :
          plans.length === 0 ? (
            <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">No study materials shared yet.</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {plans.map((p) => (
                <Card key={p.id} data-testid={`study-card-${p.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{p.title}</CardTitle>
                    <CardDescription>{p.subject} · {p.class_name} · {p.staff_name}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {p.youtube_url
                      ? <LazyYouTube url={p.youtube_url} testId={`study-player-${p.id}`} />
                      : <div className="rounded-md border bg-muted/30 p-4 text-center text-xs text-muted-foreground">No video for this material.</div>}
                    {p.notes && <p className="text-sm text-muted-foreground">{p.notes}</p>}
                    {p.file_ext && (
                      <Button size="sm" variant="outline" onClick={() => handleDownload(p)} data-testid={`study-download-${p.id}`}>
                        <Download className="mr-2 h-4 w-4" /> Download {p.file_ext.toUpperCase()}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={() => setUploading(true)} data-testid="upload-plan-btn"><Upload className="mr-2 h-4 w-4" /> Upload lesson plan</Button>
        </div>
      )}
      <Card>
        <CardContent className="p-0">
          {isPending ? <Loader2 className="m-8 h-6 w-6 animate-spin text-primary" /> :
            plans.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {isAdmin ? "No lesson plans yet. Click Upload above." : "No lesson plans assigned to you yet."}
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Title</TableHead><TableHead>Teacher</TableHead>
                  <TableHead>Class</TableHead><TableHead>Subject</TableHead>
                  <TableHead>Term</TableHead><TableHead>File</TableHead>
                  <TableHead>Video</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-semibold">{p.title}</TableCell>
                      <TableCell className="text-muted-foreground">{p.staff_name}</TableCell>
                      <TableCell>{p.class_name}</TableCell>
                      <TableCell>{p.subject}</TableCell>
                      <TableCell className="text-muted-foreground">{p.term || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.file_ext
                          ? <><Badge variant="outline">{p.file_ext.toUpperCase()}</Badge>{" "}{(p.file_size / 1024).toFixed(0)} KB</>
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {p.youtube_url
                          ? <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setVideo(p)} data-testid={`watch-btn-${p.id}`}>
                              <PlayCircle className="mr-1 h-4 w-4" /> Watch
                            </Button>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {p.file_ext && (
                            <Button size="sm" variant="ghost" onClick={() => handleDownload(p)} title="Download">
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => del.mutate(p.id)} title="Delete">
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>
      {uploading && <UploadPlanDialog onClose={() => { setUploading(false); qc.invalidateQueries({ queryKey: ["academics", "lesson-plans"] }); }} />}
      {video && video.youtube_url && <VideoDialog title={video.title} url={video.youtube_url} onClose={() => setVideo(null)} />}
    </div>
  );
}

function UploadPlanDialog({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [staffId, setStaffId] = useState("");
  const [className, setClassName] = useState("Grade 5A");
  const [subject, setSubject] = useState("");
  const [term, setTerm] = useState("");
  const [notes, setNotes] = useState("");
  const [youtube, setYoutube] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const { data: staff = [] } = useQuery({
    queryKey: ["staff"], queryFn: () => staffService.list(),
  });
  const teachers = staff.filter((s) => s.role === "teacher");

  const ytValid = !youtube.trim() || youtubeEmbedUrl(youtube) !== null;

  const mut = useMutation({
    mutationFn: () => academicsService.uploadPlan({
      title, staff_id: staffId, class_name: className, subject, term: term || undefined,
      notes: notes || undefined, youtube_url: youtube.trim() || undefined, file: file,
    }),
    onSuccess: () => { toast.success("Lesson plan saved"); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  const canSave = !!(title && staffId && className && subject && (file || youtube.trim()) && ytValid);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="upload-plan-dialog">
        <DialogHeader>
          <DialogTitle>Upload lesson plan / study material</DialogTitle>
          <DialogDescription>Attach a file (PDF/DOCX/PPTX ≤10 MB) and/or a private YouTube link. Students of the class can watch the video inside the portal.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Fractions — Term 1" data-testid="plan-title" /></div>
          <div>
            <Label>Teacher</Label>
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="plan-teacher"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select teacher…</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Class</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} data-testid="plan-class" /></div>
            <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Mathematics" data-testid="plan-subject" /></div>
          </div>
          <div><Label>Term (optional)</Label><Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Term 1 2026-27" /></div>
          <div>
            <Label className="flex items-center gap-1"><Youtube className="h-4 w-4 text-red-600" /> Private YouTube Link (optional)</Label>
            <Input value={youtube} onChange={(e) => setYoutube(e.target.value)}
              placeholder="https://youtu.be/… or https://www.youtube.com/watch?v=…" data-testid="plan-youtube" />
            {!ytValid && <p className="mt-1 text-[11px] text-destructive">That doesn't look like a valid YouTube link.</p>}
            {youtube.trim() && ytValid && (
              <div className="mt-2"><YouTubePlayer url={youtube} testId="plan-youtube-preview" /></div>
            )}
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div>
            <Label>File (PDF / DOCX / PPTX, ≤10 MB) — optional if a video link is provided</Label>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.pptx"
                   onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                   className="block w-full text-sm" data-testid="plan-file" />
            {file && <div className="mt-1 text-xs text-muted-foreground">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!canSave || mut.isPending} data-testid="plan-save">
            {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════ LOGBOOK ═══════════════════════
function LogbookTab({ user }: { user: any }) {
  const qc = useQueryClient();
  const isTeacher = user?.role === "teacher";
  const isAdmin = user?.role === "admin" || user?.role === "principal" || user?.role === "examhead";
  const [adding, setAdding] = useState(false);
  const [days, setDays] = useState(14);

  const { data: entries = [], isPending: entriesLoading } = useQuery({
    queryKey: ["academics", "logbook"],
    queryFn: () => academicsService.listLogbook({}),
  });
  const { data: compliance, isPending: compLoading } = useQuery({
    queryKey: ["academics", "logbook", "compliance", days],
    queryFn: () => academicsService.compliance(days),
    enabled: isAdmin,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{entries.length} entries</div>
        {(isTeacher || isAdmin) && (
          <Button onClick={() => setAdding(true)}><Plus className="mr-2 h-4 w-4" /> Log a period</Button>
        )}
      </div>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Teacher compliance ({days} days)</CardTitle>
            <CardDescription>
              % of scheduled days where the teacher submitted at least one logbook entry.
              <span className="ml-2">
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                        className="rounded border px-1 text-xs">
                  <option value={7}>7d</option>
                  <option value={14}>14d</option>
                  <option value={30}>30d</option>
                </select>
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {compLoading ? <Loader2 className="m-8 h-6 w-6 animate-spin text-primary" /> :
             !compliance?.teachers.length ? <div className="p-4 text-sm text-muted-foreground">No teacher data yet.</div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Teacher</TableHead>
                  <TableHead className="text-right">Expected days</TableHead>
                  <TableHead className="text-right">Submitted</TableHead>
                  <TableHead className="text-right">Compliance</TableHead>
                  <TableHead>Missing (recent)</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {compliance.teachers.map((t) => (
                    <TableRow key={t.teacher_id}>
                      <TableCell className="font-semibold">{t.teacher_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{t.expected_days}</TableCell>
                      <TableCell className="text-right tabular-nums">{t.submitted_days}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={t.compliance_pct >= 80 ? "success" : t.compliance_pct >= 50 ? "outline" : "destructive"}>
                          {t.compliance_pct}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{t.missing_dates.join(", ") || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Recent entries</CardTitle></CardHeader>
        <CardContent className="p-0">
          {entriesLoading ? <Loader2 className="m-8 h-6 w-6 animate-spin text-primary" /> :
           entries.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No entries yet.</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Period</TableHead>
                <TableHead>Teacher</TableHead><TableHead>Class</TableHead>
                <TableHead>Subject</TableHead><TableHead>Topic</TableHead>
                <TableHead>Homework</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {entries.slice(0, 100).map((e: LogbookEntry) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-muted-foreground">{e.date}</TableCell>
                    <TableCell>P{e.period}</TableCell>
                    <TableCell className="text-muted-foreground">{e.teacher_name}</TableCell>
                    <TableCell>{e.class_name}</TableCell>
                    <TableCell>{e.subject}</TableCell>
                    <TableCell>
                      <div className="font-medium">{e.topic}</div>
                      {e.subtopic && <div className="text-xs text-muted-foreground">{e.subtopic}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.homework || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {adding && <LogbookDialog onClose={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["academics", "logbook"] }); }} />}
    </div>
  );
}

function LogbookDialog({ onClose }: { onClose: () => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [period, setPeriod] = useState("1");
  const [className, setClassName] = useState("");
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");
  const [subtopic, setSubtopic] = useState("");
  const [homework, setHomework] = useState("");
  const [remarks, setRemarks] = useState("");

  const mut = useMutation({
    mutationFn: () => academicsService.submitLogbook({
      date, period: Number(period), class_name: className, subject,
      topic, subtopic: subtopic || undefined,
      homework: homework || undefined, remarks: remarks || undefined,
    }),
    onSuccess: () => { toast.success("Logbook entry saved"); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  const canSave = date && Number(period) > 0 && className && subject && topic;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log a period</DialogTitle>
          <DialogDescription>Record what you taught this period — auto-linked to your account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><Label>Period</Label><Input type="number" min={1} max={12} value={period} onChange={(e) => setPeriod(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Class</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="Grade 5A" /></div>
            <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Mathematics" /></div>
          </div>
          <div><Label>Topic</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Fractions" /></div>
          <div><Label>Sub-topic</Label><Input value={subtopic} onChange={(e) => setSubtopic(e.target.value)} /></div>
          <div><Label>Homework</Label><Input value={homework} onChange={(e) => setHomework(e.target.value)} /></div>
          <div><Label>Remarks</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!canSave || mut.isPending}>
            {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Save entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════ NOTEBOOK CHECKS ═══════════════════════
const STATUS_OPTIONS = ["Excellent", "Good", "Satisfactory", "Needs Improvement"] as const;

function NotebookTab({ user }: { user: any }) {
  const qc = useQueryClient();
  const isPrivileged = user?.role === "admin" || user?.role === "principal" || user?.role === "teacher" || user?.role === "examhead";
  const [classFilter, setClassFilter] = useState("");
  const [adding, setAdding] = useState(false);

  const { data: report, isPending: reportLoading } = useQuery({
    queryKey: ["academics", "notebook", "report", classFilter],
    queryFn: () => academicsService.notebookReport(classFilter ? { class_name: classFilter } : {}),
    enabled: isPrivileged,
  });
  const { data: checks = [], isPending: checksLoading } = useQuery({
    queryKey: ["academics", "notebook", "list", classFilter],
    queryFn: () => academicsService.listNotebookChecks(classFilter ? { class_name: classFilter } : {}),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Label className="sr-only">Class</Label>
          <Input value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
                 placeholder="Filter by class (e.g. Grade 5A) — blank = all"
                 className="w-72" />
        </div>
        {isPrivileged && (
          <Button onClick={() => setAdding(true)}><Plus className="mr-2 h-4 w-4" /> Record check</Button>
        )}
      </div>

      {isPrivileged && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Class summary {classFilter && `— ${classFilter}`}</CardTitle>
            <CardDescription>{report?.total_checks ?? 0} total checks across {report?.students.length ?? 0} student(s)</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {reportLoading ? <Loader2 className="m-8 h-6 w-6 animate-spin text-primary" /> :
             !report?.students.length ? <div className="p-4 text-sm text-muted-foreground">No checks recorded yet.</div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Student</TableHead><TableHead>Class</TableHead>
                  <TableHead className="text-right">Checks</TableHead>
                  <TableHead>Distribution</TableHead>
                  <TableHead>Last check</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {report.students.map((s) => (
                    <TableRow key={s.student_id}>
                      <TableCell className="font-semibold">{s.student_name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.class_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{s.checks}</TableCell>
                      <TableCell className="flex flex-wrap gap-1">
                        {STATUS_OPTIONS.map((st) => s.by_status[st] > 0 && (
                          <Badge key={st} variant={
                            st === "Excellent" ? "success" :
                            st === "Needs Improvement" ? "destructive" : "outline"
                          }>
                            {st}: {s.by_status[st]}
                          </Badge>
                        ))}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {s.last_checked || "—"}{s.last_remark && ` · ${s.last_remark}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Recent checks</CardTitle></CardHeader>
        <CardContent className="p-0">
          {checksLoading ? <Loader2 className="m-8 h-6 w-6 animate-spin text-primary" /> :
           checks.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No entries.</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Student</TableHead>
                <TableHead>Class</TableHead><TableHead>Subject</TableHead>
                <TableHead>Status</TableHead><TableHead>Teacher</TableHead>
                <TableHead>Remark</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {checks.slice(0, 100).map((c: NotebookCheck) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-muted-foreground">{c.date}</TableCell>
                    <TableCell className="font-semibold">{c.student_name}</TableCell>
                    <TableCell>{c.class_name}</TableCell>
                    <TableCell>{c.subject}</TableCell>
                    <TableCell>
                      <Badge variant={
                        c.status === "Excellent" ? "success" :
                        c.status === "Needs Improvement" ? "destructive" : "outline"
                      }>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{c.teacher_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.remark || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {adding && <NotebookCheckDialog classFilter={classFilter} onClose={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["academics", "notebook"] }); }} />}
    </div>
  );
}

function NotebookCheckDialog({ classFilter, onClose }: { classFilter: string; onClose: () => void }) {
  const [className, setClassName] = useState(classFilter || "Grade 5A");
  const [studentId, setStudentId] = useState("");
  const [subject, setSubject] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState("Good");
  const [remark, setRemark] = useState("");

  const { data: students = [] } = useQuery({
    queryKey: ["students", "list", className],
    queryFn: () => studentsService.list(),
  });
  const classStudents = students.filter((s) => s.class_name === className);

  const mut = useMutation({
    mutationFn: () => academicsService.addNotebookCheck({
      student_id: studentId, subject, date, status,
      remark: remark || undefined,
    }),
    onSuccess: () => { toast.success("Check recorded"); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  const canSave = studentId && subject && date && status;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record notebook check</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Class</Label><Input value={className} onChange={(e) => { setClassName(e.target.value); setStudentId(""); }} placeholder="Grade 5A" /></div>
          <div>
            <Label>Student</Label>
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select student…</option>
              {classStudents.map((s) => <option key={s.id} value={s.id}>{s.roll_no ? `${s.roll_no} — ` : ""}{s.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Mathematics" /></div>
            <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          </div>
          <div>
            <Label>Status</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Remark (optional)</Label><Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!canSave || mut.isPending}>
            {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Save check
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
